import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Calendar as CalIcon, Plus, AlertOctagon, Inbox, X, Check, GripVertical, FileDown } from "lucide-react";
import SearchableSelect from "@/components/SearchableSelect";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const MESI = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const GIORNI = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];

const URG_COLORS = {
  bassa: "bg-zinc-700 text-zinc-200",
  media: "bg-[#FF9F0A]/70 text-black",
  alta: "bg-[#FF9F0A] text-black",
  critica: "bg-[#FF3B30] text-white",
};

const TIPO_BORDER = {
  officina: "border-l-4 border-l-[#4CAF50]",
  cantiere: "border-l-4 border-l-[#1E90FF]",
  emergenza: "border-l-4 border-l-[#FF3B30]",
};

const TIPO_LABELS = {
  officina: "OFF",
  cantiere: "CANT",
  emergenza: "EMR",
};

const STATO_LABELS = {
  aperta: "APERTA", in_lavorazione: "IN LAVOR.", in_attesa_ricambi: "ATTESA RICAMBI",
  completata: "COMPLETATA", fatturata: "FATTURATA", chiusa: "CHIUSA",
};

// Stati che indicano che l'intervento è ancora "aperto" (non concluso) → se la data è passata è SALTATO
const STATI_APERTI = new Set(["aperta", "in_lavorazione", "in_attesa_ricambi"]);

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
function isOverdue(w, todayStr) {
  // salted = data prevista strettamente < oggi E stato ancora aperto
  return w.data_prevista && w.data_prevista < todayStr && STATI_APERTI.has(w.stato);
}

function firstMonday(year, month) {
  // month 1-12. Restituisce il lunedì che apre la griglia (può essere del mese precedente)
  const first = new Date(year, month-1, 1);
  const dow = (first.getDay() + 6) % 7; // 0 = lunedì
  const d = new Date(first);
  d.setDate(1 - dow);
  return d;
}

export default function CalendarPage() {
  const nav = useNavigate();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [items, setItems] = useState([]);
  const [overdueItems, setOverdueItems] = useState([]);
  const [unscheduledItems, setUnscheduledItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState(""); // ""=tutti, officina/cantiere/emergenza
  const [scheduling, setScheduling] = useState(null); // WO being scheduled { id, numero, ..., date }
  const [quickCreate, setQuickCreate] = useState(null); // {customer_id, machine_id, descrizione, urgenza, tipologia}
  const [draggingId, setDraggingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { year, month };
      if (filtro) params.tipologia = filtro;
      const [monthRes, overdueRes, unschedRes] = await Promise.all([
        api.get("/work-orders/calendar/month", { params }),
        api.get("/work-orders/calendar/overdue"),
        api.get("/work-orders/calendar/unscheduled"),
      ]);
      setItems(monthRes.data);
      setOverdueItems(overdueRes.data);
      setUnscheduledItems(unschedRes.data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/customers").then(r => setCustomers(r.data)).catch(()=>{});
  }, []);
  useEffect(() => {
    if (quickCreate?.customer_id) {
      api.get(`/machines?customer_id=${quickCreate.customer_id}`).then(r => setMachines(r.data)).catch(()=>setMachines([]));
    } else { setMachines([]); }
  }, [quickCreate?.customer_id]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, filtro]);

  const prev = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const next = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth() + 1); };

  // Assign date to a WO (from scheduling modal OR from drag&drop)
  const assignDate = async (woId, dateStr) => {
    try {
      await api.put(`/work-orders/${woId}`, { data_prevista: dateStr });
      toast.success("Scheda pianificata", {
        action: {
          label: "Notifica cliente",
          onClick: () => nav(`/schede/${woId}?wa=1`),
        },
        duration: 5000,
      });
      setScheduling(null);
      // navigate to that month
      const [y, m] = dateStr.split("-").map(Number);
      if (y && m) { setYear(y); setMonth(m); }
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore"); }
  };

  const openQuickCreate = () => setQuickCreate({ customer_id: "", machine_id: "", descrizione_problema: "", urgenza: "media", tipologia_intervento: "officina" });

  const submitQuickCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/work-orders", { ...quickCreate, tipo_garanzia: "pagamento", data_prevista: "" });
      toast.success("Richiesta aggiunta a 'Da pianificare'");
      setQuickCreate(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore"); }
  };

  // Drag & drop handlers
  const onDragStart = (id) => (e) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch { /* ignore */ }
  };
  const onDayDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDayDrop = (dateStr) => (e) => {
    e.preventDefault();
    const id = draggingId || e.dataTransfer.getData("text/plain");
    setDraggingId(null);
    if (id) assignDate(id, dateStr);
  };

  // ---------- Export PDF ----------
  const monthName = new Date(year, month-1, 1).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const exportPdf = () => {
    const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const w = doc.internal.pageSize.getWidth();
    let y = 15;
    doc.setFontSize(16); doc.setFont(undefined, "bold");
    doc.text("GERMAC OFFICINA — Calendario", 15, y);
    y += 6;
    doc.setFontSize(11); doc.setFont(undefined, "normal");
    doc.text(monthName.toUpperCase(), 15, y);
    doc.setFontSize(9);
    doc.text(`Stampato il ${new Date().toLocaleDateString("it-IT")} ${new Date().toLocaleTimeString("it-IT",{hour:"2-digit",minute:"2-digit"})}`, w - 15, y, { align: "right" });
    y += 6;

    // === Sezione DA PIANIFICARE ===
    if (unscheduledItems.length > 0) {
      doc.setFontSize(11); doc.setFont(undefined, "bold");
      doc.setTextColor(255, 159, 10);
      doc.text(`DA PIANIFICARE (${unscheduledItems.length})`, 15, y);
      doc.setTextColor(0, 0, 0);
      y += 2;
      autoTable(doc, {
        startY: y + 2,
        head: [["N°", "Cliente", "Macchina", "Problema", "Urg.", "Tipo"]],
        body: unscheduledItems.map(w => [
          w.numero || "",
          w.customer_name || "",
          w.machine_label || "",
          (w.descrizione_problema || "").slice(0, 60),
          (w.urgenza || "").toUpperCase(),
          (w.tipologia_intervento || "").toUpperCase(),
        ]),
        styles: { fontSize: 8, cellPadding: 1.5 },
        headStyles: { fillColor: [255, 159, 10], textColor: 0, fontSize: 8 },
        columnStyles: { 0: { cellWidth: 22 }, 4: { cellWidth: 14 }, 5: { cellWidth: 18 } },
        margin: { left: 15, right: 15 },
      });
      y = doc.lastAutoTable.finalY + 6;
    }

    // === Sezione DA RIPROGRAMMARE ===
    if (overdueItems.length > 0) {
      if (y > 240) { doc.addPage(); y = 15; }
      doc.setFontSize(11); doc.setFont(undefined, "bold");
      doc.setTextColor(255, 59, 48);
      doc.text(`DA RIPROGRAMMARE — INTERVENTI SALTATI (${overdueItems.length})`, 15, y);
      doc.setTextColor(0, 0, 0);
      y += 2;
      autoTable(doc, {
        startY: y + 2,
        head: [["N°", "Cliente", "Macchina", "Data prevista", "Ritardo", "Stato"]],
        body: overdueItems.map(w => {
          const dl = Math.floor((new Date(todayStr) - new Date(w.data_prevista))/(86400*1000));
          return [w.numero, w.customer_name || "", w.machine_label || "", w.data_prevista, `+${dl}g`, (STATO_LABELS[w.stato] || w.stato)];
        }),
        styles: { fontSize: 8, cellPadding: 1.5 },
        headStyles: { fillColor: [255, 59, 48], textColor: 255, fontSize: 8 },
        columnStyles: { 0: { cellWidth: 22 } },
        margin: { left: 15, right: 15 },
      });
      y = doc.lastAutoTable.finalY + 6;
    }

    // === Sezione MESE CORRENTE — schede giorno per giorno ===
    if (y > 250) { doc.addPage(); y = 15; }
    doc.setFontSize(11); doc.setFont(undefined, "bold");
    doc.setTextColor(76, 175, 80);
    doc.text(`PIANIFICATO — ${monthName.toUpperCase()} (${items.length} schede)`, 15, y);
    doc.setTextColor(0, 0, 0);
    y += 2;
    // Group by day
    const groups = {};
    items.forEach(w => { (groups[w.data_prevista] = groups[w.data_prevista] || []).push(w); });
    const sortedDays = Object.keys(groups).sort();
    const rows = [];
    sortedDays.forEach(day => {
      const dItems = groups[day];
      dItems.forEach((w, idx) => {
        const dayIt = day ? new Date(day).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit" }) : "";
        rows.push([
          idx === 0 ? dayIt : "",
          w.numero,
          w.customer_name || "",
          w.machine_label || "",
          (w.tipologia_intervento || "").toUpperCase(),
          (w.urgenza || "").toUpperCase(),
          STATO_LABELS[w.stato] || w.stato,
        ]);
      });
    });
    if (rows.length === 0) {
      doc.setFontSize(9); doc.setFont(undefined, "italic"); doc.setTextColor(120);
      doc.text("Nessun intervento pianificato nel mese.", 15, y + 6);
      y += 10;
    } else {
      autoTable(doc, {
        startY: y + 2,
        head: [["Giorno", "N°", "Cliente", "Macchina", "Tipo", "Urg.", "Stato"]],
        body: rows,
        styles: { fontSize: 8, cellPadding: 1.5 },
        headStyles: { fillColor: [76, 175, 80], textColor: 255, fontSize: 8 },
        columnStyles: { 0: { cellWidth: 22 }, 1: { cellWidth: 22 } },
        margin: { left: 15, right: 15 },
      });
    }

    doc.save(`GERMAC-Calendario-${year}-${String(month).padStart(2, "0")}.pdf`);
  };

  // Build 6-week grid (42 cells)
  const start = firstMonday(year, month);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }

  // Group items by yyyy-mm-dd
  const byDay = {};
  items.forEach(it => {
    const k = it.data_prevista;
    if (!k) return;
    (byDay[k] = byDay[k] || []).push(it);
  });

  const isToday = (d) => d.toDateString() === today.toDateString();
  const isCurrentMonth = (d) => d.getMonth() === (month - 1);
  const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  const todayStr = useMemo(() => todayIso(), []);

  return (
    <div className="p-4 md:p-8 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Pianificazione</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight flex items-center gap-3">
            <CalIcon className="text-[#4CAF50]" size={32} /> Calendario Interventi
          </h1>
        </div>
        <Link to="/schede" className="gm-btn-primary h-11 px-5 rounded-sm flex items-center gap-2" data-testid="calendar-new-wo">
          <Plus size={16} /> Nuova scheda
        </Link>
        <button
          onClick={exportPdf}
          className="h-11 px-4 border border-zinc-700 hover:border-[#4CAF50] hover:text-[#4CAF50] uppercase text-xs tracking-widest font-bold rounded-sm flex items-center gap-2"
          data-testid="calendar-export-pdf"
          title="Stampa PDF del mese + Da pianificare + Saltate"
        >
          <FileDown size={14}/> Stampa PDF
        </button>
      </div>

      {/* Alert schede SALTATE (data prevista passata + stato aperto) */}
      {overdueItems.length > 0 && (
        <div className="gm-card p-4 mb-4 border-2 border-[#FF3B30]/70 bg-[#FF3B30]/5" data-testid="calendar-overdue-alert">
          <div className="flex items-start gap-3 mb-3">
            <div className="h-10 w-10 bg-[#FF3B30]/20 border border-[#FF3B30]/50 flex items-center justify-center shrink-0 rounded-sm">
              <AlertOctagon className="text-[#FF3B30]" size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-widest text-[#FF3B30] font-bold">Da riprogrammare</div>
              <h3 className="font-display text-2xl font-black uppercase leading-tight">{overdueItems.length} intervent{overdueItems.length === 1 ? "o saltato" : "i saltati"}</h3>
              <div className="text-xs text-zinc-400 mt-1">Data prevista passata ma la scheda è ancora aperta. Riprogramma o completa.</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {overdueItems.slice(0, 12).map(w => {
              const daysLate = Math.floor((new Date(todayStr) - new Date(w.data_prevista)) / (1000*60*60*24));
              return (
                <div
                  key={w.id}
                  data-testid="calendar-overdue-item"
                  className="text-left p-2 border border-[#FF3B30]/40 bg-[#0a0a0a] hover:bg-[#FF3B30]/10 rounded-sm"
                >
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-widest mb-1">
                    <span className="font-mono-data text-[#FF3B30] font-bold">{w.numero}</span>
                    <span className="text-[#FF3B30] font-bold">+{daysLate}g in ritardo</span>
                  </div>
                  <button
                    onClick={()=>nav(`/schede/${w.id}`)}
                    className="text-left w-full"
                    data-testid="calendar-overdue-open"
                  >
                    <div className="text-xs font-semibold truncate">{w.customer_name || "—"}</div>
                    <div className="text-[11px] text-zinc-400 truncate">{w.machine_label || "—"}</div>
                    <div className="text-[10px] text-zinc-500 mt-1">
                      Previsto: <span className="font-mono-data text-zinc-300">{w.data_prevista}</span> · {STATO_LABELS[w.stato] || w.stato}
                    </div>
                  </button>
                  <div className="mt-2 pt-2 border-t border-[#FF3B30]/20 flex items-center gap-1.5">
                    <input
                      type="date"
                      min={todayStr}
                      defaultValue={todayStr}
                      onChange={(e)=>{ e.target.dataset.newdate = e.target.value; }}
                      onClick={(e)=>e.stopPropagation()}
                      className="gm-input h-8 py-1 flex-1 text-xs"
                      data-testid="overdue-date-input"
                    />
                    <button
                      onClick={(e)=>{
                        e.stopPropagation();
                        const input = e.currentTarget.previousSibling;
                        const val = input.dataset.newdate || input.defaultValue || input.value;
                        if (!val) { toast.error("Scegli una data"); return; }
                        assignDate(w.id, val);
                      }}
                      className="h-8 px-2 bg-[#FF9F0A] hover:bg-[#FF9F0A]/80 text-black uppercase text-[10px] tracking-widest font-bold rounded-sm whitespace-nowrap"
                      data-testid="overdue-reschedule"
                      title="Riprogramma"
                    >
                      Riprogramma
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          {overdueItems.length > 12 && (
            <div className="text-xs text-zinc-400 mt-2">+ altri {overdueItems.length - 12} — <Link to="/schede?stato=aperta" className="text-[#FF3B30] hover:underline uppercase tracking-widest font-bold">Vedi tutte</Link></div>
          )}
        </div>
      )}

      {/* Pannello DA PIANIFICARE (schede senza data_prevista) */}
      <div className="gm-card p-4 mb-4 border-[#FF9F0A]/40" data-testid="calendar-unscheduled-panel">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 bg-[#FF9F0A]/15 border border-[#FF9F0A]/50 flex items-center justify-center shrink-0 rounded-sm">
              <Inbox className="text-[#FF9F0A]" size={20} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#FF9F0A] font-bold">Da pianificare</div>
              <h3 className="font-display text-2xl font-black uppercase leading-tight">
                {unscheduledItems.length} richiest{unscheduledItems.length === 1 ? "a" : "e"} senza data
              </h3>
              <div className="text-xs text-zinc-400 mt-0.5">Trascina su un giorno del calendario oppure clicca per assegnare la data.</div>
            </div>
          </div>
          <button
            onClick={openQuickCreate}
            className="gm-btn-primary h-11 px-4 rounded-sm flex items-center gap-2"
            data-testid="calendar-quick-create"
          >
            <Plus size={16} /> Aggiungi richiesta
          </button>
        </div>

        {unscheduledItems.length === 0 ? (
          <div className="text-center py-4 text-sm text-zinc-500 border border-dashed border-zinc-800 rounded-sm">
            Nessuna richiesta in attesa. Ottimo lavoro.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {unscheduledItems.slice(0, 16).map(w => (
              <div
                key={w.id}
                draggable
                onDragStart={onDragStart(w.id)}
                onDragEnd={()=>setDraggingId(null)}
                onClick={()=>setScheduling({ ...w, date: todayStr })}
                data-testid="calendar-unscheduled-item"
                className={`text-left p-2 border rounded-sm bg-[#0a0a0a] cursor-grab active:cursor-grabbing hover:bg-[#FF9F0A]/10 transition ${
                  draggingId === w.id ? "opacity-40 border-[#FF9F0A]" : "border-[#FF9F0A]/30"
                } ${TIPO_BORDER[w.tipologia_intervento] || ""}`}
                title="Trascina su un giorno OR clicca per assegnare data"
              >
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest mb-1">
                  <span className="flex items-center gap-1 font-mono-data text-[#FF9F0A] font-bold">
                    <GripVertical size={10} className="text-zinc-600" /> {w.numero}
                  </span>
                  <span className={`gm-badge ${w.urgenza === "critica" ? "gm-badge-danger" : w.urgenza === "alta" ? "gm-badge-warn" : "gm-badge-muted"} text-[9px]`}>
                    {w.urgenza}
                  </span>
                </div>
                <div className="text-xs font-semibold truncate">{w.customer_name || "—"}</div>
                <div className="text-[11px] text-zinc-400 truncate">{w.machine_label || "—"}</div>
                {w.descrizione_problema && (
                  <div className="text-[10px] text-zinc-500 mt-1 line-clamp-2">{w.descrizione_problema}</div>
                )}
              </div>
            ))}
          </div>
        )}
        {unscheduledItems.length > 16 && (
          <div className="text-xs text-zinc-400 mt-2">+ altri {unscheduledItems.length - 16}</div>
        )}
      </div>

      {/* Filtri tipologia */}
      <div className="gm-card p-3 mb-3 flex gap-2 flex-wrap">
        {[
          ["", "Tutti", "bg-zinc-700"],
          ["officina", "Officina", "bg-[#4CAF50]"],
          ["cantiere", "Cantiere", "bg-[#1E90FF]"],
          ["emergenza", "Emergenze", "bg-[#FF3B30]"],
        ].map(([v, l, c]) => (
          <button key={v} onClick={()=>setFiltro(v)}
            data-testid={`cal-filter-${v || "all"}`}
            className={`px-4 h-10 border uppercase text-xs tracking-widest font-bold flex items-center gap-2 ${
              filtro === v ? "bg-[#4CAF50] text-white border-[#4CAF50]" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
            }`}>
            <span className={`w-2 h-2 ${c} inline-block`}/> {l}
          </button>
        ))}
      </div>

      {/* Navigation */}
      <div className="gm-card p-4 mb-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button onClick={prev} className="h-10 w-10 border border-zinc-700 hover:border-white flex items-center justify-center rounded-sm" data-testid="cal-prev">
            <ChevronLeft size={18} />
          </button>
          <button onClick={goToday} className="h-10 px-4 border border-zinc-700 hover:border-[#4CAF50] hover:text-[#4CAF50] uppercase text-xs tracking-widest font-bold" data-testid="cal-today">
            Oggi
          </button>
          <button onClick={next} className="h-10 w-10 border border-zinc-700 hover:border-white flex items-center justify-center rounded-sm" data-testid="cal-next">
            <ChevronRight size={18} />
          </button>
        </div>
        <div className="font-display text-3xl font-black uppercase tracking-tight">
          {MESI[month-1]} <span className="text-zinc-500">{year}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-zinc-400 flex-wrap">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-zinc-700"/> Bassa</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-[#FF9F0A]/70"/> Media</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-[#FF9F0A]"/> Alta</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-[#FF3B30]"/> Critica</span>
          <span className="flex items-center gap-1.5 text-[#FF3B30] font-bold"><span className="w-3 h-3 bg-[#FF3B30] gm-pulse"/> Saltata</span>
        </div>
      </div>

      {/* Grid */}
      <div className="gm-card overflow-hidden">
        <div className="grid grid-cols-7 border-b border-zinc-800 bg-zinc-900/50">
          {GIORNI.map(g => (
            <div key={g} className="p-2 text-center text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{g}</div>
          ))}
        </div>
        {loading ? (
          <div className="p-8 text-zinc-500 text-center">Caricamento...</div>
        ) : (
          <div className="grid grid-cols-7">
            {cells.map((d, i) => {
              const k = isoDay(d);
              const dayItems = byDay[k] || [];
              const inMonth = isCurrentMonth(d);
              const isDropTarget = !!draggingId;
              return (
                <div key={i}
                  data-testid="cal-day"
                  onDragOver={isDropTarget ? onDayDragOver : undefined}
                  onDrop={isDropTarget ? onDayDrop(k) : undefined}
                  className={`min-h-[110px] border-b border-r border-zinc-800/60 p-1.5 ${inMonth ? "" : "bg-zinc-900/30"} ${isToday(d) ? "bg-[#4CAF50]/5" : ""} ${isDropTarget ? "outline-dashed outline-1 outline-[#FF9F0A]/40 hover:bg-[#FF9F0A]/10" : ""}`}
                >
                  <div className={`text-xs font-bold mb-1 flex items-center justify-between ${isToday(d) ? "text-[#4CAF50]" : inMonth ? "text-zinc-300" : "text-zinc-600"}`}>
                    <span>{d.getDate()}</span>
                    {isToday(d) && <span className="text-[9px] uppercase tracking-widest text-[#4CAF50]">OGGI</span>}
                  </div>
                  <div className="space-y-1">
                    {dayItems.slice(0, 4).map(w => {
                      const salted = isOverdue(w, todayStr);
                      const chipClass = salted
                        ? "bg-[#FF3B30] text-white border-l-4 border-l-[#FF3B30] ring-1 ring-[#FF3B30] gm-pulse"
                        : `${URG_COLORS[w.urgenza] || URG_COLORS.media} ${TIPO_BORDER[w.tipologia_intervento] || TIPO_BORDER.officina}`;
                      return (
                        <button
                          key={w.id}
                          onClick={() => nav(`/schede/${w.id}`)}
                          data-testid={salted ? "cal-wo-chip-overdue" : "cal-wo-chip"}
                          className={`w-full text-left text-[10px] leading-tight px-1.5 py-1 rounded-sm truncate ${chipClass} hover:opacity-90`}
                          title={`${salted ? "⚠ SALTATA · " : ""}[${TIPO_LABELS[w.tipologia_intervento]||"?"}] ${w.numero} — ${w.customer_name} — ${w.machine_label} (${STATO_LABELS[w.stato] || w.stato})`}
                        >
                          <div className="font-mono-data font-bold truncate flex items-center gap-1">
                            {salted && <AlertOctagon size={9} strokeWidth={3} />}
                            {w.numero}
                          </div>
                          <div className="truncate opacity-90">{w.customer_name}</div>
                        </button>
                      );
                    })}
                    {dayItems.length > 4 && (
                      <div className="text-[10px] text-zinc-500 uppercase tracking-widest px-1">
                        +{dayItems.length - 4} altri
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Legenda stati */}
      <div className="mt-4 text-xs text-zinc-500">
        Le schede sono quelle con <b className="text-zinc-300">{"\u201C"}Data prevista intervento{"\u201D"}</b> compilata. Click su una scheda per aprirla.
      </div>

      {/* Modal: assegna data a scheda "da pianificare" */}
      {scheduling && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="schedule-modal">
          <div className="gm-card p-5 w-full max-w-md">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#FF9F0A] font-bold">Pianifica intervento</div>
                <h3 className="font-display text-xl font-black uppercase">{scheduling.numero}</h3>
              </div>
              <button onClick={()=>setScheduling(null)} className="text-zinc-400 hover:text-white p-1"><X size={20}/></button>
            </div>
            <div className="text-sm mb-1"><b>{scheduling.customer_name}</b></div>
            <div className="text-xs text-zinc-400 mb-3">{scheduling.machine_label}</div>
            {scheduling.descrizione_problema && (
              <div className="text-xs text-zinc-500 mb-3 line-clamp-3 border-l-2 border-zinc-800 pl-2">{scheduling.descrizione_problema}</div>
            )}
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Data prevista *</label>
            <input
              type="date"
              value={scheduling.date}
              onChange={e=>setScheduling(s=>({...s, date: e.target.value}))}
              className="gm-input mb-3"
              data-testid="schedule-date-input"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={()=>assignDate(scheduling.id, scheduling.date)}
                disabled={!scheduling.date}
                className="gm-btn-primary h-11 px-5 rounded-sm flex items-center gap-2"
                data-testid="schedule-confirm"
              >
                <Check size={16} /> Pianifica
              </button>
              <Link to={`/schede/${scheduling.id}`} className="h-11 px-4 border border-zinc-700 hover:border-white uppercase text-xs tracking-widest font-bold flex items-center">
                Apri scheda
              </Link>
              <button onClick={()=>setScheduling(null)} className="h-11 px-4 border border-zinc-700 uppercase text-xs tracking-widest font-bold">Annulla</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: creazione rapida richiesta */}
      {quickCreate && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="quick-create-modal">
          <form onSubmit={submitQuickCreate} className="gm-card p-5 w-full max-w-xl my-8">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#FF9F0A] font-bold">Nuova richiesta</div>
                <h3 className="font-display text-xl font-black uppercase">Da pianificare</h3>
              </div>
              <button type="button" onClick={()=>setQuickCreate(null)} className="text-zinc-400 hover:text-white p-1"><X size={20}/></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Cliente *</label>
                <SearchableSelect
                  required
                  testId="quick-customer"
                  value={quickCreate.customer_id}
                  onChange={(v)=>setQuickCreate(s=>({...s, customer_id: v, machine_id: ""}))}
                  placeholder="— Cliente * (scrivi per cercare)"
                  options={customers.map(c => ({ value: c.id, label: c.ragione_sociale, subLabel: c.piva || c.referente || "" }))}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Macchina *</label>
                <SearchableSelect
                  required
                  testId="quick-machine"
                  value={quickCreate.machine_id}
                  onChange={(v)=>setQuickCreate(s=>({...s, machine_id: v}))}
                  disabled={!quickCreate.customer_id}
                  placeholder={quickCreate.customer_id ? "— Macchina * (scrivi per cercare)" : "Seleziona prima un cliente"}
                  options={machines.map(m => ({ value: m.id, label: `${m.marca} ${m.modello}`, subLabel: m.matricola }))}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Problema segnalato *</label>
                <textarea
                  required
                  rows={3}
                  value={quickCreate.descrizione_problema}
                  onChange={e=>setQuickCreate(s=>({...s, descrizione_problema: e.target.value}))}
                  placeholder="Descrizione problema riportato dal cliente"
                  className="gm-input"
                  data-testid="quick-descrizione"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Urgenza</label>
                <select value={quickCreate.urgenza} onChange={e=>setQuickCreate(s=>({...s, urgenza: e.target.value}))} className="gm-input" data-testid="quick-urgenza">
                  <option value="bassa">Bassa</option>
                  <option value="media">Media</option>
                  <option value="alta">Alta</option>
                  <option value="critica">Critica</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Tipologia</label>
                <select value={quickCreate.tipologia_intervento} onChange={e=>setQuickCreate(s=>({...s, tipologia_intervento: e.target.value}))} className="gm-input" data-testid="quick-tipologia">
                  <option value="officina">Officina</option>
                  <option value="cantiere">Cantiere</option>
                  <option value="emergenza">Emergenza</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 pt-4 mt-3 border-t border-zinc-800">
              <button type="submit" className="gm-btn-primary h-11 px-5 rounded-sm flex items-center gap-2" data-testid="quick-create-submit">
                <Plus size={16} /> Aggiungi a {"\u201C"}Da pianificare{"\u201D"}
              </button>
              <button type="button" onClick={()=>setQuickCreate(null)} className="h-11 px-4 border border-zinc-700 uppercase text-xs tracking-widest font-bold">Annulla</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
